import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useGetModelFamiliesModelFamilyGet, useGetModelFamilyTagsModelFamilyTagsGet } from "../../../../api/generated/fastAPI";
import { ModelFamily, ModelFamilyListResponse } from "../../../../api/models";
import SearchBar from "../../../../components/Filtering/SearchBar";
import FilterBar from "../../../../components/Filtering/FilterBar";
import useDataParams, { GetParamsTypes } from "../../../../hooks/useDataParams";
import NewPopupTabs from "../../../../components/Tabs/NewPopupTabs";
import SelectField from "../../../../components/Form/SelectFields";
import { defaultModelParams, defaultFilters } from "../../../../utils/supplementalData";
import PaginatedSelectList, { SelectListOption } from "../../../../components/Filtering/PaginatedSelectList";
import { customInstance } from "../../../../api/custom-instance";

const tabList = ['Individual Model Selection', 'Adverse Outcome Pathway (AOP)'];

export default function SelectModelsV2({
    preselectedModelFamilyIds,
    selectedIndex,
    setSelectedIndex,
    selectedModels,
    setSelectedModels,
    getModelLoadingState,
    isPrefetching,
}: {
    preselectedModelFamilyIds?: number[],
    selectedIndex?: number,
    setSelectedIndex?: Dispatch<SetStateAction<number>>,
    selectedModels: ModelFamily[],
    setSelectedModels: Dispatch<SetStateAction<ModelFamily[]>>,
    getModelLoadingState?: (isLoading: boolean) => void,
    isPrefetching: boolean,
}) {
    const getModelFamilyTags = useGetModelFamilyTagsModelFamilyTagsGet();
    const [ selectedTag, setSelectedTag ] = useState('');

    const {
        params,
        setParams,
        search,
        filters,
        setPaginationLimit,
        setPaginationOffset,
    } = useDataParams({
        defaultParams: defaultModelParams,
        defaultFilters,
        tagsData: getModelFamilyTags.data as unknown as { [key: string]: string[] },
    })

    const getModelFamiliesMutation = useGetModelFamiliesModelFamilyGet(params as GetParamsTypes);
    const [ modelFamilies, setModelFamilies ] = useState<ModelFamily[]>([]);
    const [ count, setCount ] = useState(0);

    useEffect(() => {
        if (getModelFamiliesMutation.data) {
            const response = getModelFamiliesMutation.data as unknown as ModelFamilyListResponse;
            const data = (response.model_families ?? []) as unknown as ModelFamily[];
            setModelFamilies(data);
            setCount(response.count ?? 0);
        }
    }, [getModelFamiliesMutation.data]);

    useEffect(() => {
        if (getModelLoadingState) {
            getModelLoadingState(getModelFamiliesMutation.isLoading);
        }
    }, [getModelLoadingState, getModelFamiliesMutation.isLoading]);


    const availableOptions: SelectListOption[] = useMemo(() => {
        if (modelFamilies.length === 0) return [];
        return modelFamilies.map(model => ({ id: model.id!, name: model.name ?? '' }))
    }, [modelFamilies]);

    const chosenOptions: SelectListOption[] = useMemo(() => {
        if (selectedModels.length === 0) return [];
        return selectedModels.map(model => ({ id: model.id!, name: model.name ?? '' }))
    }, [selectedModels]);

    const addToSelectedModels = useCallback((
        id: number | string,
    ) => {
        if (modelFamilies.length === 0) {
            return;
        }

        const chosenModelFamily = modelFamilies.find(model => model.id === Number(id));

        if (chosenModelFamily) {
            setSelectedModels((prevModels) => prevModels.concat(chosenModelFamily));
        }
    }, [modelFamilies, setSelectedModels]);

    const removeFromSelectedModels = useCallback((
        id: number | string
    ) => {
        setSelectedModels((prevModels) => prevModels.filter(model => model.id !== Number(id)));
    }, [setSelectedModels]);
    

    const handleTabChange = (e:any, index: number) => {
        if (setSelectedIndex) {
            setSelectedIndex(index);
            setSelectedModels([]);
            setParams(defaultModelParams);
            setSelectedTag('');
        }
    }

    const aopTags = useMemo(() => {
        if (!getModelFamilyTags.data) return [];

        let aopIdTags: string[] = [];

        const tags = getModelFamilyTags.data as unknown as { [key: string]: string[] };
        const aopTagGroup = Object.entries(tags).find(tag => tag[0] === 'aop_id');
        if (aopTagGroup && aopTagGroup[1].length > 0) {
            aopTagGroup[1].forEach(tagValue => {
                aopIdTags.push(`${aopTagGroup[0]}::${tagValue}`)
            })
        }

        return aopIdTags;
    }, [getModelFamilyTags.data]);

    const handleSelection = useCallback(async (e:any, name: string) => {
        const tag = e.value as string;
        setSelectedTag(tag);

        const tagGroup = JSON.stringify([[tag]]);

        try {
            const response = await customInstance({
                url: `model_family/?tag_groups=${tagGroup}&include_model_stats=false&include_airflow_metadata=false`,
                method: 'GET',
            });
            const data = response as unknown as ModelFamilyListResponse;
            setSelectedModels((data.model_families ?? []) as unknown as ModelFamily[]);
        } catch (err:any) {
            console.log(err);
        }
      
    }, [setSelectedModels]);


    return (
        <>
            {selectedIndex !== undefined && (
                <NewPopupTabs 
                    tabs={tabList.map((tab, i) => ({
                        label: tab,
                        index: i,
                    }))}
                    selectedIndex={selectedIndex}
                    handleTabChange={handleTabChange}
                />
            )}

            {selectedIndex === undefined || selectedIndex === 0 ? (
                <div className="flex flex-col gap-10">
                    <div className="flex flex-col justify-start items-start gap-4">
                        <SearchBar
                            label="Search"
                            searchFn={search}
                            searchKey="name"
                        />
                        <FilterBar 
                            filters={filters}
                            setParams={setParams}
                            params={params}
                        />
                    </div>
                    <PaginatedSelectList 
                        availableOptions={availableOptions} 
                        chosenOptions={chosenOptions}
                        addOption={addToSelectedModels}
                        removeOption={removeFromSelectedModels}
                        isMultiSelect={true}
                        isLoading={getModelFamiliesMutation.isLoading || isPrefetching}
                        isCountLoading={getModelFamiliesMutation.isLoading}
                        count={count}
                        setPaginationLimit={setPaginationLimit}
                        setPaginationOffset={setPaginationOffset}
                        params={params as GetParamsTypes}
                        showSelected={preselectedModelFamilyIds !== undefined ? true : false}
                    />
                </div>
            ) : (
                <>
                    <SelectField 
                        label="Adverse Outcome"
                        id='aop-selection'
                        name='aop-selection'
                        value={selectedTag ? { value: selectedTag, label: selectedTag } : null}
                        options={aopTags.length > 0 ? aopTags.map(tag => ({ value: tag, label: tag })) : []}
                        isCreatable={false}
                        isMulti={false}
                        isRequired={false}
                        menuPortalTarget={document.getElementById('single-prediction-popup')}
                        handleChange={handleSelection}
                    />
                </>
            )} 
            
        </>
    )
}